/**
 * @fileoverview PUT handler for the "/engagement/:app_group_id/library_replace_file" endpoint.
 * Uses FormData for file upload (multipart/form-data).
 */
import fetch from "@lib/fetch";

import {
  createMutationFetcherFunctionWithShadowError,
  reactQueryMutationAdapter,
} from "../reactQueryFetcherAdapters";
import { engagementAppGroupIdLibraryReplaceFileCacheKeys } from "./constants";
import type {
  PutEngagementAppGroupIdLibraryReplaceFileProps,
  EngagementAppGroupIdLibraryReplaceFileResult,
  ReplaceFileErrors,
} from "./types";

/**
 * Replaces the file on an existing media library asset.
 *
 * @throws {ReplaceFileErrors} Use {@link isReplaceFileError} to narrow in catch blocks:
 * ```ts
 * try {
 *   await mutateAsync(props);
 * } catch (error) {
 *   if (isReplaceFileError(error as import("@lib/errors").FetchRequestError)) {
 *     switch (error.errorCode) {
 *       case "EXTENSION_MISMATCH":
 *         // error.meta.expected_extension, error.meta.received_extension
 *         break;
 *       case "ASSET_SIZE_EXCEEDS_LIMIT":
 *         // error.meta.size_limit_bytes, error.meta.file_size_bytes
 *         break;
 *     }
 *   }
 * }
 * ```
 */
export const putEngagementAppGroupIdLibraryReplaceFile = reactQueryMutationAdapter(
  engagementAppGroupIdLibraryReplaceFileCacheKeys,
  createMutationFetcherFunctionWithShadowError<ReplaceFileErrors>()(
    (
      props: PutEngagementAppGroupIdLibraryReplaceFileProps
    ): Promise<EngagementAppGroupIdLibraryReplaceFileResult> => {
      const body = new FormData();
      body.append("file", props.file);
      body.append("id", props.id);

      return fetch(
        `/engagement/${props.app_group_id}/library_replace_file`,
        "PUT",
        {},
        { body },
        { setContentType: false, propagateFetchRequestError: false }
      );
    }
  )
);
